import { shopifyComponentCopy } from '@testfeed/product-language'
import type { HomeData, PlanState } from './types'

export type StepId = 'connect' | 'plan' | 'sync'

export type StepStatus =
  | 'todo' // ready for the merchant to act
  | 'locked' // waits on an earlier step
  | 'working' // TestFeed is doing something
  | 'problem' // needs the merchant again
  | 'done'

export const hasPlan = (p: PlanState) => p.status === 'active' || p.status === 'trial'

export function stepStatus(data: HomeData, id: StepId): StepStatus {
  switch (id) {
    case 'connect':
      if (data.connect.status === 'granted') return 'done'
      return data.connect.status === 'declined' ? 'problem' : 'todo'
    case 'plan':
      if (hasPlan(data.plan)) return 'done'
      return data.plan.status === 'cancelled' ? 'problem' : 'todo'
    case 'sync': {
      const s = data.sync.status
      if (s === 'done') return 'done'
      if (s === 'syncing') return 'working'
      if (data.connect.status !== 'granted' || !hasPlan(data.plan)) return 'locked'
      return s === 'failed' ? 'problem' : 'todo'
    }
  }
}

export const STEP_ORDER: StepId[] = ['connect', 'plan', 'sync']

export function setupProgress(data: HomeData) {
  const done = STEP_ORDER.filter((id) => stepStatus(data, id) === 'done').length
  return { done, total: STEP_ORDER.length, complete: done === STEP_ORDER.length }
}

/** The step to open by default: the first one that isn't done. */
export function currentStep(data: HomeData): StepId | null {
  return STEP_ORDER.find((id) => stepStatus(data, id) !== 'done') ?? null
}

/** Tests can only run once shoppers exist and a plan is active. */
export function canRunTests(data: HomeData) {
  return data.sync.status === 'done' && hasPlan(data.plan)
}

export function usage(data: HomeData) {
  if (!hasPlan(data.plan)) return null
  const p = data.plan as Extract<PlanState, { status: 'active' | 'trial' }>
  const left = Math.max(0, p.testsIncluded - p.testsUsed)
  return {
    used: p.testsUsed,
    included: p.testsIncluded,
    left,
    atLimit: left === 0,
    nearLimit: left > 0 && p.testsUsed / p.testsIncluded >= 0.8,
  }
}

export type BannerId =
  | 'stale'
  | 'resync-failed'
  | 'resyncing'
  | 'sync-failed'
  | 'plan-cancelled'
  | 'limit-reached'
  | 'near-limit'
  | 'trial-ending'
  | 'few-orders'

export interface BannerSpec {
  id: BannerId
  tone: 'info' | 'warning' | 'critical'
  heading: string
  body: string
  action?: { label: string; do: 'retry-load' | 'retry-sync' | 'resync' | 'choose-plan' | 'upgrade' }
  /** Progress bar under the text, 0 to 100. */
  progress?: number
  dismissible: boolean
}

/**
 * Shopify's homepage template: only one banner at a time.
 * Returns the most urgent banner that applies, or null.
 * Setup problems that the guide already shows (declined permission, no plan yet) don't get a banner.
 *
 * Banner text here is assembled from multiple branch-dependent, interpolated values rather than a
 * single fixed template, so — unlike the rest of this page's copy — it stays inline instead of moving
 * into the product-language dictionary. See docs/CODESTYLE-COMPONENTS.md.
 */
export function pickBanner(data: HomeData, stale?: { savedAt: string }): BannerSpec | null {
  const closed = new Set(data.prefs.dismissedBanners)
  const u = usage(data)
  const candidates: (BannerSpec | false)[] = [
    !!stale && {
      id: 'stale',
      tone: 'warning',
      heading: "Couldn't refresh",
      body: `Showing what we had at ${stale.savedAt}.`,
      action: { label: 'Try again', do: 'retry-load' },
      dismissible: false,
    },
    // The open guide already shows this, so the banner is for when the guide is out of sight.
    data.sync.status === 'failed' &&
      (data.prefs.guideDismissed || data.prefs.guideCollapsed) && {
        id: 'sync-failed',
        tone: 'critical',
        heading: 'Shopper sync stopped',
        body: data.sync.reason,
        action: { label: 'Retry sync', do: 'retry-sync' },
        dismissible: false,
      },
    data.sync.status === 'done' &&
      data.sync.refresh?.status === 'failed' && {
        id: 'resync-failed',
        tone: 'warning',
        heading: "Couldn't resync store data",
        body: `${data.sync.refresh.reason} Tests keep using shoppers from ${data.sync.lastSynced}.`,
        action: { label: 'Try again', do: 'resync' },
        dismissible: false,
      },
    data.plan.status === 'cancelled' &&
      data.sync.status === 'done' && {
        id: 'plan-cancelled',
        tone: 'warning',
        heading: 'Testing is paused',
        body: `Your ${data.plan.previousPlan} plan ended. Your shoppers and results are kept.`,
        action: { label: 'Choose a plan', do: 'choose-plan' },
        dismissible: false,
      },
    !!u?.atLimit && {
      id: 'limit-reached',
      tone: 'warning',
      heading: `All ${u.included} tests used this month`,
      body: 'Upgrade to keep testing, or wait for next month.',
      action: { label: 'Upgrade plan', do: 'upgrade' },
      dismissible: false,
    },
    data.sync.status === 'done' &&
      data.sync.refresh?.status === 'running' && {
        id: 'resyncing',
        tone: 'info',
        heading: 'Resyncing store data',
        body: "Tests keep using your current shoppers until it's done.",
        progress: data.sync.refresh.progress ?? undefined,
        dismissible: false,
      },
    !!u?.nearLimit && {
      id: 'near-limit',
      tone: 'info',
      heading: `${u.left} ${u.left === 1 ? 'test' : 'tests'} left this month`,
      body: `You've used ${u.used} of ${u.included}.`,
      action: { label: 'See plans', do: 'upgrade' },
      dismissible: true,
    },
    data.plan.status === 'trial' &&
      (data.plan.trialDaysLeft ?? 99) <= 3 && {
        id: 'trial-ending',
        tone: 'info',
        heading: `Your free trial ends in ${data.plan.trialDaysLeft} ${data.plan.trialDaysLeft === 1 ? 'day' : 'days'}`,
        body: `You'll move to the paid ${data.plan.name} plan. Nothing to do.`,
        dismissible: true,
      },
    data.sync.status === 'done' &&
      data.sync.fewOrders && {
        id: 'few-orders',
        tone: 'info',
        heading: 'Your store is new to us',
        body: 'With few orders so far, shoppers also draw on stores like yours. They sharpen as you sell.',
        dismissible: true,
      },
  ]
  for (const c of candidates) if (c && !(c.dismissible && closed.has(c.id))) return c
  return null
}

export type PrimaryAction = { label: string; do: 'new-test' } | { label: string; do: 'upgrade' } | null

/** One primary action per page. During setup the guide's current step owns it. */
export function primaryAction(data: HomeData): PrimaryAction {
  const copy = shopifyComponentCopy.appHome
  if (!canRunTests(data)) return null
  if (usage(data)?.atLimit) return { label: copy.primaryUpgradeAction, do: 'upgrade' }
  return { label: copy.primaryNewTestAction, do: 'new-test' }
}

/** Resync is offered once shoppers exist and nothing is already reading store data. */
export function canResync(data: HomeData) {
  return data.sync.status === 'done' && data.connect.status === 'granted' && data.sync.refresh?.status !== 'running'
}

export const VERDICT_LABEL = {
  clear: shopifyComponentCopy.appHome.verdictClear,
  leaning: shopifyComponentCopy.appHome.verdictLeaning,
  tie: shopifyComponentCopy.appHome.verdictTie,
} as const

/** Missing counts/timestamps are not an empty store or a zero shopper pool. */
export function connectedStoreBody(connection: Extract<HomeData['connect'], { status: 'granted' }>): string {
  const copy = shopifyComponentCopy.appHome
  if (connection.products === null || connection.orders === null) return copy.connectGrantedNoCountsBody
  return copy.connectGrantedBody
    .replace('{products}', String(connection.products))
    .replace('{orders}', connection.orders.toLocaleString())
}

export function syncReadyBody(sync: Extract<HomeData['sync'], { status: 'done' }>): string {
  const copy = shopifyComponentCopy.appHome
  if (sync.shoppers === null || sync.orders === null || !sync.lastSynced) return copy.syncDoneNoCountsBody
  return copy.syncDoneBody
    .replace('{shoppers}', String(sync.shoppers))
    .replace('{orders}', sync.orders.toLocaleString())
    .replace('{lastSynced}', sync.lastSynced)
}
